import type { InteractionBlueprintData } from "@/lib/contentParsers/interactionBlueprintSchema";
import { isCategoryTraitType } from "@/types/metadataTraitType";
import { collectCtaIndexFields } from "./collectCtaIndexFields";
import {
  collectWysiwygTextsFromAdvisor,
  collectWysiwygTextsFromContent,
} from "./collectWysiwygTexts";
import { normalizeTextForSearch } from "../normalize/normalizeTextForSearch";
import { normalizeCategoryLabel } from "@/lib/sparxs/normalizeCategoryLabel";
import type { SearchIndexRow } from "../../types/searchTypes";

const encodeTraitFingerprint = (trait: {
  type: string;
  name?: string | undefined;
  product?: string | undefined;
}): string => {
  const typePart = trait.type.trim();
  const namePart = (trait.name ?? "").trim();
  const productPart =
    typeof trait.product === "string" ? trait.product.trim() : "";
  const typeNameCore =
    typePart.length > 0 && namePart.length > 0
      ? `${typePart}:${namePart}`
      : typePart.length > 0
        ? typePart
        : namePart;
  if (productPart.length > 0) {
    return `${typeNameCore}|${productPart}`;
  }
  return typeNameCore;
};

export interface SearchListMeta {
  fileName: string;
  title: string;
  key: string;
  /** `fileInfoFingerprint` of the live list `FileInfo` at index build time. */
  listFingerprint: string;
  listLastModified?: number;
}

export const buildSearchIndexRow = (
  blueprint: InteractionBlueprintData,
  listMeta: SearchListMeta,
): SearchIndexRow => {
  const { content } = blueprint;
  const traitsList = blueprint.metadata?.traits ?? [];

  const fileName =
    listMeta.fileName.length > 0
      ? listMeta.fileName
      : blueprint.fileName != null && blueprint.fileName.length > 0
        ? blueprint.fileName
        : "";
  const { title, key, listFingerprint, listLastModified } = listMeta;

  const rawCategory = traitsList.find((trait) =>
    isCategoryTraitType(trait.type),
  )?.name;
  let category: string | undefined;
  if (rawCategory != null && rawCategory.trim().length > 0) {
    const n = normalizeCategoryLabel(rawCategory);
    if (n.length > 0) {
      category = n;
    }
  }

  const traitFingerprints: string[] = [];
  for (const trait of traitsList) {
    if (isCategoryTraitType(trait.type)) {
      continue;
    }
    const fp = encodeTraitFingerprint({
      type: trait.type,
      name: trait.name,
      product: trait.product,
    });
    if (fp.length > 0) {
      traitFingerprints.push(fp);
    }
  }

  const sectionTypeCounts: Partial<Record<string, number>> = {};
  for (const item of content) {
    const t = item.type;
    sectionTypeCounts[t] = (sectionTypeCounts[t] ?? 0) + 1;
  }

  const {
    ctaTypes,
    ctaCount,
    ctaKeys,
    ctaTexts,
    redirectTos,
    redirectServiceSnippets,
  } = collectCtaIndexFields(content);

  const wysiwygParts = [
    ...collectWysiwygTextsFromContent(content),
    ...collectWysiwygTextsFromAdvisor(blueprint.advisorContent?.summary?.text),
  ];
  const normalizedPieces = wysiwygParts.map((raw) =>
    normalizeTextForSearch(raw),
  );
  const normalizedContentTextBlob = normalizedPieces.filter(Boolean).join(" ");

  return {
    fileName,
    title,
    key,
    listFingerprint,
    ...(listLastModified !== undefined ? { listLastModified } : {}),
    category,
    traitFingerprints,
    ctaTypes,
    ctaCount,
    ctaKeys,
    ctaTexts,
    redirectTos,
    redirectServiceSnippets,
    sectionTypeCounts,
    contentCount: content.length,
    normalizedContentTextBlob,
  };
};
